/**
 * Decoding for USB HID Point-of-Sale scales (HID usage page 0x8D).
 *
 * This is a real, published standard — "HID Point of Sale Usage Tables" — and
 * it's what the Dymo M-series (M5/M10/M25), Stamps.com, and most USB postal and
 * bench scales speak. That matters for us in two ways:
 *
 * 1. These scales are **HID, not serial**. `navigator.serial` never sees them,
 * and `navigator.usb` can't claim them either — on macOS and Windows the OS
 * HID driver owns the interface, so `claimInterface` fails. WebHID is the
 * only browser API that can talk to one.
 * 2. Because it's a standard, decoding is vendor-agnostic. Nothing here is
 * Dymo-specific; a different HID scale on the bench works with no changes.
 *
 * The device pushes an input report on every weight change — there is no
 * polling and no command to send. We decode the report and hand up a reading.
 * @module hid-pos-scale
 */

/** Dymo's USB vendor ID, used to widen the device picker's filter list. */
export const DYMO_VENDOR_ID = 0x0922;

/** HID usage page for Point-of-Sale scale devices. */
export const SCALE_USAGE_PAGE = 0x8d;

/**
 * Weight status, from the HID POS "Data Scale Status" usage.
 *
 * `stableAtZero` and `stable` are the two we act on: a reading is only safe to
 * capture into a weight field when the platter has settled.
 */
export type ScaleStatus =
  | 'fault'
  | 'stableAtZero'
  | 'inMotion'
  | 'stable'
  | 'underZero'
  | 'overWeight'
  | 'needsCalibration'
  | 'needsRezeroing'
  | 'unknown';

const STATUS_BY_CODE: Record<number, ScaleStatus> = {
  1: 'fault',
  2: 'stableAtZero',
  3: 'inMotion',
  4: 'stable',
  5: 'underZero',
  6: 'overWeight',
  7: 'needsCalibration',
  8: 'needsRezeroing',
};

/**
 * Conversion factors to pounds, keyed by the HID POS unit code.
 *
 * Deliberately partial. The spec defines exotic units (carats, taels,
 * pennyweight) that no scale we'd put on a packing bench reports; leaving them
 * out means an unexpected unit surfaces as an explicit "unsupported unit" the
 * operator can act on, rather than a silently wrong number in a weight field.
 */
/**
 * The international pound, exact by definition — every metric conversion below
 * derives from this one number rather than carrying its own rounded literal.
 */
const KG_PER_POUND = 0.45359237;
const POUNDS_PER_KG = 1 / KG_PER_POUND;

const POUNDS_PER_UNIT: Record<number, number> = {
  0x01: POUNDS_PER_KG / 1e6, // milligram
  0x02: POUNDS_PER_KG / 1e3, // gram
  0x03: POUNDS_PER_KG, // kilogram
  0x08: POUNDS_PER_KG * 1e3, // metric ton
  0x0b: 1 / 16, // ounce
  0x0c: 1, // pound
};

/** Human-readable unit names, for the "switch your scale to lb" message. */
const UNIT_NAMES: Record<number, string> = {
  0x01: 'mg',
  0x02: 'g',
  0x03: 'kg',
  0x04: 'carat',
  0x05: 'taels',
  0x06: 'grain',
  0x07: 'pennyweight',
  0x08: 'metric ton',
  0x09: 'ton',
  0x0a: 'troy oz',
  0x0b: 'oz',
  0x0c: 'lb',
};

/** A single decoded weight report. */
export interface ScaleReading {
  /** Weight converted to pounds, or `null` when the unit isn't supported. */
  pounds: number | null;
  /** Whether the platter has settled — the gate on capturing a weight. */
  stable: boolean;
  status: ScaleStatus;
  /** The unit the scale itself reported, e.g. `lb`, `kg`. */
  unitLabel: string;
  /** Raw HID unit code, kept so callers can detect an unsupported unit. */
  unitCode: number;
}

/**
 * Decode one HID POS scale input report.
 *
 * The report body is five bytes, after the report ID has been stripped (WebHID
 * hands the ID over separately on the event):
 *
 * ```
 * [0] status   [1] unit   [2] exponent (int8)   [3..4] value (uint16 LE)
 * ```
 *
 * The exponent is a signed power of ten — a Dymo M10 in pound mode reports
 * `0xFE` (-2), so a raw 1234 is 12.34 lb. Reading it unsigned is the classic
 * way to get a weight 10^254 times too large, hence `getInt8`.
 * @param data - Report body, report ID already removed.
 * @returns The decoded reading, or `null` if the report is too short to be one.
 */
export function decodeScaleReport(data: DataView): ScaleReading | null {
  if (data.byteLength < 5) return null;

  const statusCode = data.getUint8(0);
  const unitCode = data.getUint8(1);
  const exponent = data.getInt8(2);
  const rawValue = data.getUint16(3, /* littleEndian */ true);

  const status = STATUS_BY_CODE[statusCode] ?? 'unknown';
  const factor = POUNDS_PER_UNIT[unitCode];
  const value = rawValue * 10 ** exponent;

  return {
    // An unsupported unit still yields a status — the UI can say "switch the
    // scale to lb" instead of going dark.
    pounds:
      factor === undefined ? null : Math.round(value * factor * 1000) / 1000,
    stable: status === 'stable' || status === 'stableAtZero',
    status,
    unitLabel: UNIT_NAMES[unitCode] ?? `unit ${unitCode}`,
    unitCode,
  };
}

/**
 * Whether a device advertises a HID POS scale collection.
 *
 * Chromium's device picker will happily hand back whatever the operator clicked
 * on, so we re-check the collections rather than trusting the filter.
 * @param device - Device returned from the picker or a prior grant.
 * @returns Whether the device speaks the HID POS scale usage page.
 */
export function isScaleDevice(device: HIDDevice): boolean {
  return device.collections.some((c) => c.usagePage === SCALE_USAGE_PAGE);
}

/**
 * Filters for `navigator.hid.requestDevice`.
 *
 * A filter list is a union, so this shows both anything speaking the scale
 * usage page and any Dymo device. The second entry is a safety net: a few Dymo
 * units under-report their collections until opened, and without it they'd be
 * missing from the picker with no explanation.
 */
export const SCALE_DEVICE_FILTERS: HIDDeviceFilter[] = [
  { usagePage: SCALE_USAGE_PAGE },
  { vendorId: DYMO_VENDOR_ID },
];

/**
 * A status the operator needs to fix on the hardware before a weight can land.
 * @param reading - Latest reading, if any.
 * @returns A short message to surface, or `null` when nothing's wrong.
 */
export function scaleStatusMessage(
  reading: ScaleReading | null,
): string | null {
  if (!reading) return null;
  if (reading.pounds === null) {
    return `Scale is set to ${reading.unitLabel} — switch it to lb or kg`;
  }
  switch (reading.status) {
    case 'fault':
      return 'Scale reports a fault';
    case 'underZero':
      return 'Scale is under zero — tare it';
    case 'overWeight':
      return 'Over capacity';
    case 'needsCalibration':
      return 'Scale needs calibration';
    case 'needsRezeroing':
      return 'Scale needs re-zeroing — tare it';
    default:
      return null;
  }
}
